import React, { useContext, useMemo, useState } from 'react'
import moment from 'moment'
import { Grid, GridCell, Text, View } from 'oio-react'
import { useParams } from 'react-router-dom'
import { LAYOUT_BOX } from 'config/constants/ui'
import { initiativeUrl } from 'config/constants/urls'
import ContainerBlock from 'src/apps/CustomApp/blocks/Container'
import LayoutContainer from 'src/apps/CustomApp/components/LayoutContainer'
import Title from 'src/apps/CustomApp/components/Title'
import Titlebar from 'src/apps/CustomApp/components/Titlebar'
import ViewModeSwitchControl from 'src/apps/CustomApp/components/ViewModeSwitchControl'
import EventListItemCard from 'src/apps/Events/components/ListItemCard'
import EventListItemRow from 'src/apps/Events/components/ListItemRow'
import InitiativeCreateControl from 'src/apps/Initiative/components/CreateControl'
import InitiativeList from 'src/apps/Initiative/components/List'
import { useOrganization } from 'src/graphql/hooks'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'

const EventHome = () => {
   const { initiativeTypeSlug } = useParams()
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const isBoxGL = theme.tmpGlobalLayout === LAYOUT_BOX

   // View Mode - Grid/List View
   const [viewMode, setViewMode] = useState('grid')
   let gridColumns = isBoxGL
      ? '1[a] 2[b] 3[c] 3[d] 4[e-f]'
      : '1[a] 2[b] 3[c] 4[d] 5[e-f]'

   if (viewMode === 'list') {
      gridColumns = '1'
   }

   const gridSpacing = viewMode === 'grid' ? '15px' : '0px'
   const ListItemComponent = viewMode === 'grid'
      ? EventListItemCard
      : EventListItemRow

   const { organization } = useOrganization()
   const eventInitiativeTypes = organization.initiativeTypes
      .filter(t => t.enabled && t.class === 'event')

   // If there is an initiativeTypeSlug, show only events of that type
   // Otherwise, we assume it is a full Events app and show all events of
   // any initiative type
   const initiativeTypes = useMemo(() => {
      if (initiativeTypeSlug) {
         const initiativeType = organization.initiativeTypes
            .find(type => type.slug === initiativeTypeSlug)

         if (initiativeType) {
            return [initiativeType]
         }
      }

      return organization.initiativeTypes
         .filter(type => type.enabled && type.class === 'event')
   }, [organization.initiativeTypes, initiativeTypeSlug])

   const initiativeTypeIds = initiativeTypes.map(type => type.id)
   const appTitle = initiativeTypeSlug && initiativeTypes[0]
      ? initiativeTypes[0].namePlural
      : 'Events'

   // Only refire date filters every minute to prevent unecessary re-renders
   const nowRoundedDown = useMemo(() => (
      moment().startOf('minute').toISOString()
   ), [(new Date()).getMinutes()])

   const upcomingEventsFilter = useMemo(() => ({
      archived: 'exclude',
      drafts: 'include',
      sortBy: 'startDateAsc',
      typeIds: initiativeTypeIds,
      limit: 30,
      startDateAfter: nowRoundedDown
   }), [initiativeTypeIds, nowRoundedDown])

   const pastEventsFilter = useMemo(() => ({
      archived: 'exclude',
      drafts: 'include',
      sortBy: 'startDateDesc',
      typeIds: initiativeTypeIds,
      limit: 30,
      startDateBefore: nowRoundedDown
   }), [initiativeTypeIds, nowRoundedDown])

   return (
      <LayoutContainer>
         <ContainerBlock>
            <Titlebar>
               <Title color={theme.tmpContentTitleColor}>{appTitle}</Title>
               <View display="flex">
                  <ViewModeSwitchControl
                     currentViewMode={viewMode}
                     onListButtonClick={() => setViewMode('list')}
                     onGridButtonClick={() => setViewMode('grid')}
                  />
                  {organization.currentUserCanEdit && (
                     <View
                        display="block"
                        height="30px"
                        borderRight={`1px solid ${theme.tmpContentLineColor}`}
                        marginRight="15px"
                        marginLeft="15px"
                     />
                  )}
                  <InitiativeCreateControl initiativeTypes={eventInitiativeTypes} />
               </View>
            </Titlebar>
            <View
               marginTop={viewMode === 'grid' && '20px[a-d] 30px[e-f]'}
               marginBottom={viewMode === 'list' && '40px'}>
               <Grid columns={gridColumns} spacing={gridSpacing}>
                  <InitiativeList
                     emptyContent={(
                        <GridCell colspan={gridColumns}>
                           <View
                              width="100%"
                              padding={viewMode === 'grid'
                                 ? '10px 0px 50px 0px'
                                 : '30px 0px 50px 0px'
                              }>
                              <Text size="2" color="#888">
                                 {`There are no upcoming ${appTitle} to show`}
                              </Text>
                           </View>
                        </GridCell>
                     )}
                     filter={upcomingEventsFilter}>
                     {initiativeList => initiativeList.items.map((initiative) => {
                        const attendeeRole = initiative.roles.find(r => r.type === 'primary')

                        return (
                           <GridCell key={initiative.id}>
                              <ListItemComponent
                                 borderColor={theme.tmpContentLineColor}
                                 coverMedia={initiative.coverMedia}
                                 date={initiative.startDateTimeISO8601}
                                 linkTo={initiativeUrl(initiative)}
                                 location={initiative.location}
                                 name={initiative.name}
                                 numAttendees={attendeeRole.numActiveUsers}
                                 privacy={initiative.privacy}
                                 subtitle={initiative.subtitle}
                                 summary={initiative.body.summary}
                                 startDate={initiative.startDate}
                                 startTime={initiative.startTime}
                                 textColor={theme.tmpNavTextColor}
                                 textSize={theme.tmpBodyTextSize}
                                 virtual={initiative.eventIsVirtual}
                              />
                           </GridCell>
                        )
                     })}
                  </InitiativeList>
               </Grid>
            </View>
            <View marginBottom="40px">
               <View
                  display="flex"
                  alignItems="center"
                  height="50px"
                  borderBottom={`1px solid ${theme.tmpContentLineColor}`}
                  marginBottom={viewMode === 'grid' && '20px[a-d] 30px[e-f]'}>
                  <Text color={theme.tmpTitleColor} size="5">
                     {`Past ${appTitle}`}
                  </Text>
               </View>
               <Grid columns={gridColumns} spacing={gridSpacing}>
                  <InitiativeList
                     emptyContent={(
                        <GridCell colspan={gridColumns}>
                           <View
                              width="100%"
                              padding={viewMode === 'grid'
                                 ? '10px 0px 50px 0px'
                                 : '30px 0px 50px 0px'
                              }>
                              <Text size="2" color="#888">No events to show</Text>
                           </View>
                        </GridCell>
                     )}
                     filter={pastEventsFilter}>
                     {initiativeList => initiativeList.items.map((initiative) => {
                        const attendeeRole = initiative.roles.find(r => r.type === 'primary')

                        return (
                           <GridCell key={initiative.id}>
                              <ListItemComponent
                                 borderColor={theme.tmpContentLineColor}
                                 coverMedia={initiative.coverMedia}
                                 date={initiative.startDateTimeISO8601}
                                 linkTo={initiativeUrl(initiative)}
                                 location={initiative.location}
                                 name={initiative.name}
                                 numAttendees={attendeeRole.numActiveUsers}
                                 privacy={initiative.privacy}
                                 subtitle={initiative.subtitle}
                                 summary={initiative.body.summary}
                                 startDate={initiative.startDate}
                                 startTime={initiative.startTime}
                                 textColor={theme.tmpNavTextColor}
                                 textSize={theme.tmpBodyTextSize}
                                 virtual={initiative.eventIsVirtual}
                                 pastEvent
                              />
                           </GridCell>
                        )
                     })}
                  </InitiativeList>
               </Grid>
            </View>
         </ContainerBlock>
      </LayoutContainer>
   )
}

export default EventHome
